/**
 * Square grid renderer
 * Requirements: 6.1, 6.4
 */

import type { Position } from '@langton/shared';
import { BaseRenderer } from './base-renderer';

/**
 * Renderer for square grid cells
 * Draws cells as squares in a regular grid pattern
 */
export class SquareRenderer extends BaseRenderer {
  /**
   * Square grid has 4 directions
   */
  getDirectionCount(): number {
    return 4;
  }

  /**
   * Get angle for direction (0=up, 1=right, 2=down, 3=left)
   * Returns angle in radians where 0 = pointing up
   */
  getDirectionAngle(direction: number): number {
    // 0=up (0°), 1=right (90°), 2=down (180°), 3=left (270°)
    return (direction * Math.PI) / 2;
  }

  /**
   * Calculate cell position in canvas coordinates
   */
  getCellPosition(x: number, y: number, cellSize: number): Position {
    return {
      x: x * cellSize,
      y: y * cellSize
    };
  }

  /**
   * Draw a square cell
   * Requirements: 6.1, 6.4
   */
  drawCell(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    color: string,
    cellSize: number
  ): void {
    const pos = this.getCellPosition(x, y, cellSize);
    
    ctx.fillStyle = color;
    ctx.fillRect(pos.x, pos.y, cellSize, cellSize);

    // Draw subtle grid lines
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
    ctx.lineWidth = 1;
    ctx.strokeRect(pos.x, pos.y, cellSize, cellSize);
  }

  /**
   * Square grids don't need avatar clipping
   */
  getAvatarClipPath(
    _ctx: CanvasRenderingContext2D,
    _centerX: number,
    _centerY: number,
    _size: number
  ): Path2D | null {
    return null;
  }
}
